import { useQuery, type QueryStatus } from "@tanstack/react-query";
import { Eye } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { LockedContent } from "@/components/layout/locked-content";

import { getScoreBarColor, getScoreBadgeColor } from "@/lib/chart";
import { cn, message, formatSnakeCase } from "@/lib/utils";
import { BrandIntelApiFactory } from "@/apis/brand-os/brand-intel";
import type { CreativeScoresAPIData } from "@/schema/brand-os/brand-intel";

interface VisualMetric {
  label: string;
  status: string;
  score: number;
}

function scoreStatus(score: number): string {
  if (score >= 80) return "Consistent";
  if (score >= 60) return "Minor Drift";
  return "Inconsistent";
}

function extractMetrics(data: CreativeScoresAPIData): VisualMetric[] {
  const scores = data.average_scores;
  const metrics: VisualMetric[] = [];
  for (const [key, raw] of Object.entries(scores)) {
    if (key.endsWith("_status")) continue;
    if (typeof raw === "string") continue;
    const score = Math.round(raw * 100);
    if (score <= 0) continue;
    const statusKey = `${key}_status`;
    const siblingStatus = scores[statusKey];
    const status = typeof siblingStatus === "string" ? siblingStatus : scoreStatus(score);
    const label = formatSnakeCase(key);
    metrics.push({ label, status, score });
  }
  return metrics;
}

function MetricRow({ metric }: { metric: VisualMetric }) {
  const badge = getScoreBadgeColor(metric.score);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-foreground truncate">{metric.label}</p>
        <div className="flex items-center gap-2 shrink-0">
          <Badge variant="secondary" className={cn(badge.bg, badge.text)}>
            {metric.status}
          </Badge>
          <p className="text-sm font-medium text-foreground w-6 text-right">{metric.score}</p>
        </div>
      </div>
      <Progress value={metric.score} className="h-2 bg-secondary" indicatorClassName={getScoreBarColor(metric.score)} />
    </div>
  );
}

function MetricsContent({ metrics }: { metrics: VisualMetric[] }) {
   if (!metrics.length) return <LockedContent message={message} className="mb-10" />;
      return (
        <div className="space-y-6">
          {metrics.map((metric) => (
            <MetricRow key={metric.label} metric={metric} />
          ))}
        </div>
      );
}

function VisualIdentityAnalysisContent({ status, metrics }: { status: QueryStatus; metrics: VisualMetric[] }) {
  switch (status) {
    case "pending":
      return <MetricsSkeleton />;
    case "error":
      return <LockedContent message={message} className="mb-10" />;
    case "success":
      return <MetricsContent metrics={metrics} />;
  }
}

export function VisualIdentityAnalysis() {
  const { data, status } = useQuery(BrandIntelApiFactory.Queries.FetchCreativeScores());
  const metrics = data ? extractMetrics(data) : [];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center gap-2">
        <div className="rounded-md bg-violet-50 p-2">
          <Eye className="size-5 text-violet-600" strokeWidth={2.5} />
        </div>
        <CardTitle className="text-base font-semibold">Visual Identity Analysis</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <VisualIdentityAnalysisContent status={status} metrics={metrics} />
      </CardContent>
    </Card>
  );
}

function MetricsSkeleton() {
  return (
    <div className="space-y-6">
      {Array.from({ length: 3 }, (_, i) => (
        <div key={i} className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <Skeleton className="h-4 w-24" />
            <div className="flex items-center gap-2">
              <Skeleton className="h-5 w-16 rounded-full" />
              <Skeleton className="h-4 w-6" />
            </div>
          </div>
          <Skeleton className="h-2 w-full rounded-full" />
        </div>
      ))}
    </div>
  );
}
